import type { IndexSet } from './path.ts';

/**
 * The island chest: what the reward disc at a pier's end holds. The prize is chosen from the
 * profile and the set alone, so it is the same every time that chest is opened (and across
 * reloads and devices) yet looks random from one island to the next. Nothing about it is stored:
 * the chest's only state is the reward's own progress key.
 */

/** Share of chests that hold a shiny sticker, out of 100; the rest hold the bonus game. */
export const STICKER_PERCENT = 40;

export type ChestPrize = { type: 'game' } | { type: 'sticker'; char: string };

/** 32-bit FNV-1a, then a murmur-style finaliser so close-together seeds land far apart. */
export function hashSeed(seed: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  h ^= h >>> 16;
  h = Math.imul(h, 0x85ebca6b);
  h ^= h >>> 13;
  h = Math.imul(h, 0xc2b2ae35);
  h ^= h >>> 16;
  return h >>> 0;
}

/** The set's words that have a real picture: the only ones a sticker can be made of. */
export function stickerChars(set: Pick<IndexSet, 'pictures'>): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const p of set.pictures) {
    if (!p.picture || seen.has(p.char)) continue;
    seen.add(p.char);
    out.push(p.char);
  }
  return out;
}

/**
 * What the chest on `setId` holds for this profile. Never a sticker when there is nothing to
 * stick; `chars` is `stickerChars` of the island.
 */
export function chestPrize(profileId: string, setId: string, chars: readonly string[]): ChestPrize {
  if (chars.length === 0) return { type: 'game' };
  const h = hashSeed(`${profileId}|${setId}|chest`);
  if (h % 100 >= STICKER_PERCENT) return { type: 'game' };
  // The roll above used the low bits; pick the word from the higher ones.
  const char = chars[(h >>> 8) % chars.length]!;
  return { type: 'sticker', char };
}
